import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { api } from '@/services/api'
import { useToast } from '@/components/ui/ToastContainer'
import type { PaymentRequest } from '@stripe/stripe-js'
import { getPaymentMethods } from '@/api/paymentMethods'
import type { PaymentMethod } from '@/types/paymentMethod'
import SavedCardSelector from '../wallet/SavedCardSelector'

interface Product {
  id: string
  title: string
  price: number
  previewUrl?: string
}

interface PurchaseProductModalProps {
  product: Product
  onClose: () => void
  onSuccess: () => void
}

export default function PurchaseProductModal({ product, onClose, onSuccess }: PurchaseProductModalProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { showSuccess, showError } = useToast()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<'digital' | 'card'>('card') // card = default, digital = Apple/Google Pay

  // Saved cards state
  const [savedCards, setSavedCards] = useState<PaymentMethod[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [saveCard, setSaveCard] = useState(true) // Pre-checked opt-out checkbox
  const [isLoadingCards, setIsLoadingCards] = useState(true)

  // Wallet state
  const [walletBalance, setWalletBalance] = useState<number>(0)
  const [useWallet, setUseWallet] = useState(true) // Default to using wallet if available
  // v2 (owner 11-04: "it flashes through 3 screens"): the payment part appears ONCE, after saved cards, wallet balance and
  // the Apple/Google Pay check have all answered (each answered separately before -> 3 quick layouts). Never waits > 2.5 s.
  const [walletLoaded, setWalletLoaded] = useState(false)
  const [walletPayChecked, setWalletPayChecked] = useState(false)
  const [waitedLong, setWaitedLong] = useState(false)
  useEffect(() => {
    const a = setTimeout(() => setWalletPayChecked(true), 1500)
    const b = setTimeout(() => setWaitedLong(true), 2500)
    return () => { clearTimeout(a); clearTimeout(b) }
  }, [])

  // Fetch wallet balance
  const fetchWalletBalance = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch(`${api.baseURL}/api/wallet/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setWalletBalance(data.balance || 0)
      }
    } catch (err) {
      console.error('Failed to fetch wallet balance:', err)
      setWalletBalance(0)
    } finally {
      setWalletLoaded(true)
    }
  }

  // Fetch saved payment methods and wallet balance on mount
  useEffect(() => {
    const fetchSavedCards = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (!token) return

        const response = await getPaymentMethods(token)
        setSavedCards(response.payment_methods || [])

        // Auto-select default card if exists
        const defaultCard = response.payment_methods.find(pm => pm.is_default)
        if (defaultCard) {
          setSelectedCardId(defaultCard.id)
        }
      } catch (err) {
        console.error('Failed to fetch saved cards:', err)
        // On error, ensure we show the new card form
        setSavedCards([])
        setSelectedCardId(null)
      } finally {
        setIsLoadingCards(false)
      }
    }

    fetchSavedCards()
    fetchWalletBalance()
  }, [])

  // Dismiss keyboard when modal opens (mobile support)
  useEffect(() => {
    // Blur any active input to dismiss mobile keyboard
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }, []) // Run on mount since modal is only rendered when open

  // Initialize Payment Request (for Apple Pay / Google Pay)
  useEffect(() => {
    if (!stripe) return

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Content Unlock',
        amount: Math.round(product.price * 100) // Convert to cents
      },
      requestPayerName: true,
      requestPayerEmail: true
    })

    // Check if Payment Request is available (Apple Pay / Google Pay)
    pr.canMakePayment().then((result) => {
      if (result) {
        // Check if Apple Pay or Google Pay is specifically available
        const hasApplePay = result.applePay === true
        const hasGooglePay = result.googlePay === true

        if (hasApplePay || hasGooglePay) {
          setPaymentRequest(pr)
          setPaymentMethod('digital') // Default to digital wallet
        } else {
          // Link or other methods available but not Apple/Google Pay
          // Don't show the digital wallet tab - just use card
          setPaymentMethod('card')
        }
      } else {
        setPaymentMethod('card') // Fallback to card if no digital wallets
      }
    }).catch(() => {}).finally(() => setWalletPayChecked(true))

    // Handle payment method event
    pr.on('paymentmethod', async (e) => {
      try {
        setIsProcessing(true)

        // Create payment intent
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`${api.baseURL}/api/purchases/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ product_id: product.id })
        })

        if (!response.ok) {
          throw new Error('Failed to initiate purchase')
        }

        const { client_secret } = await response.json()

        // Confirm payment with payment method from wallet
        const { error: confirmError } = await stripe.confirmCardPayment(
          client_secret,
          { payment_method: e.paymentMethod.id },
          { handleActions: false }
        )

        if (confirmError) {
          e.complete('fail')
          throw new Error(confirmError.message)
        }

        e.complete('success')

        // Success!
        showSuccess('🎉 Purchase successful! Product unlocked!', 5000)
        onClose()
        onSuccess()
      } catch (err: any) {
        e.complete('fail')
        console.error('Purchase error:', err)
        showError(err.message || 'Purchase failed')
      } finally {
        setIsProcessing(false)
      }
    })

  }, [stripe, product])

  // Handle card payment (both saved and new cards)
  const handleCardPurchase = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setIsProcessing(true)
      setError(null)

      const token = localStorage.getItem('auth_token')

      // Calculate wallet vs card split
      const walletAmount = useWallet ? Math.min(walletBalance, product.price) : 0
      const fullWalletPayment = walletAmount >= product.price

      // If wallet covers the full amount, no card needed
      if (fullWalletPayment) {
        const response = await fetch(`${api.baseURL}/api/purchases/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            product_id: product.id,
            use_wallet: true,
            wallet_amount: walletAmount
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to process wallet payment')
        }

        const data = await response.json()

        if (data.payment_status === 'succeeded') {
          showSuccess('🎉 Purchase successful! Product unlocked!', 5000)
          onClose()
          onSuccess()
          return
        }
      }

      // Need card payment (possibly with partial wallet)
      if (!stripe) {
        setError('Payment system not loaded')
        return
      }

      // Using saved payment method (one-click purchase)
      if (savedCards.length > 0 && selectedCardId && selectedCardId !== 'new') {
        const response = await fetch(`${api.baseURL}/api/purchases/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            product_id: product.id,
            saved_payment_method_id: selectedCardId,
            use_wallet: useWallet && walletAmount > 0,
            wallet_amount: walletAmount
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to initiate purchase')
        }

        const data = await response.json()

        // Check if payment succeeded immediately (no webhook needed)
        if (data.payment_status === 'succeeded') {
          showSuccess('🎉 Purchase successful! Product unlocked!', 5000)
          onClose()
          onSuccess()
          return
        }

        // Backend auto-confirms payment with saved card
        // Poll for webhook completion (max 10 seconds) - only if not immediately succeeded
        let attempts = 0
        const maxAttempts = 20 // 20 attempts * 500ms = 10 seconds

        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 500))

          try {
            // Check if purchase succeeded
            const checkResponse = await fetch(`${api.baseURL}/api/purchases/check/${product.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              }
            })

            if (checkResponse.ok) {
              const checkData = await checkResponse.json()
              if (checkData.has_purchased) {
                showSuccess('🎉 Purchase successful! Product unlocked!', 5000)
                onClose()
                onSuccess()
                return
              }
            }
          } catch (err) {
            console.error('Error checking purchase status:', err)
          }

          attempts++
        }

        // Timeout - payment may still process via webhook
        throw new Error('Payment is processing. Please refresh in a moment.')
      }

      // Using new card
      if (!elements) {
        setError('Payment form not loaded')
        return
      }

      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        setError('Please enter your card details')
        return
      }

      // 1. Create payment intent with save_payment_method flag and wallet params
      const response = await fetch(`${api.baseURL}/api/purchases/initiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: product.id,
          save_payment_method: saveCard, // User's opt-in/out choice
          use_wallet: useWallet && walletAmount > 0,
          wallet_amount: walletAmount
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to initiate purchase')
      }

      const { client_secret } = await response.json()

      // 2. Confirm payment with new card
      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement
        }
      })

      if (result.error) {
        setError(result.error.message || 'Payment failed')
        showError(result.error.message || 'Payment failed')
      } else {
        // Success!
        showSuccess('🎉 Purchase successful! Product unlocked!', 5000)
        onClose()
        onSuccess()
      }
    } catch (err: any) {
      console.error('Purchase error:', err)
      const errorMessage = err.message || 'Purchase failed'
      setError(errorMessage)
      showError(errorMessage)
    } finally {
      setIsProcessing(false)
    }
  }

  const modalContent = (
    <>
      {/* Black backdrop layer */}
      <div
        className="fixed inset-0"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 9999
        }}
        onClick={onClose}
      />

      {/* Modal content layer - completely isolated */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          zIndex: 10000,
          pointerEvents: 'none'
        }}
      >
        <div style={{pointerEvents: 'auto', position: 'relative', zIndex: 1, width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto'}}>
          <div
            className="rounded-2xl w-full p-4 sm:p-8 shadow-2xl"
            style={{
              backgroundColor: '#FFFFFF',
              backgroundImage: 'none',
              backgroundClip: 'padding-box'
            }}
            onClick={(e) => e.stopPropagation()}
          >
        {/* Header */}
        <div className="text-center mb-6">
          {product.previewUrl ? (
            <div className="relative mx-auto mb-4 overflow-hidden rounded-xl shadow-lg max-h-[35vh] sm:max-h-[45vh]">
              <img
                src={product.previewUrl}
                alt="Preview"
                className="w-full h-auto max-h-[35vh] sm:max-h-[45vh] object-cover"
                style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
              />
              {/* Backdrop blur overlay to match chat interface */}
              <div
                className="absolute inset-0 rounded-xl"
                style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
              />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-gray-100">
              <svg className="w-8 h-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          )}
          <p className="text-lg text-gray-400">
            Unlock for {product.price % 1 === 0 ? `$${product.price}` : `$${product.price.toFixed(2)}`}
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-2 border-red-200 rounded-xl p-4 text-red-700 text-sm">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {!((!isLoadingCards && walletLoaded && walletPayChecked) || waitedLong) ? (
          <div className="v2-pay-skel" role="status" aria-label="Loading payment options"><span /><span /><span /></div>
        ) : (<>
        {/* Payment Method Tabs */}
        {paymentRequest && (
          <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setPaymentMethod('digital')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                paymentMethod === 'digital'
                  ? 'text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={paymentMethod === 'digital' ? {background: 'linear-gradient(135deg, #ffc9e3 0%, #ffb3d9 100%)'} : undefined}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Apple / Google Pay
              </span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod('card')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                paymentMethod === 'card'
                  ? 'text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={paymentMethod === 'card' ? {background: 'linear-gradient(135deg, #ffc9e3 0%, #ffb3d9 100%)'} : undefined}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                </svg>
                New
              </span>
            </button>
          </div>
        )}

        {/* Wallet Balance Toggle (show if user has wallet balance) */}
        {walletBalance > 0 && paymentMethod !== 'digital' && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="text-sm text-green-800 font-medium">
                  Use wallet balance (${walletBalance.toFixed(2)})
                </span>
              </div>
              <input
                type="checkbox"
                checked={useWallet}
                onChange={(e) => setUseWallet(e.target.checked)}
                className="w-5 h-5 text-green-600 focus:ring-green-500 rounded"
              />
            </label>
            {useWallet && walletBalance >= product.price && (
              <p className="text-xs text-green-600 mt-2">
                ✓ Your wallet covers the full amount - no card needed!
              </p>
            )}
            {useWallet && walletBalance > 0 && walletBalance < product.price && (
              <p className="text-xs text-green-600 mt-2">
                ${walletBalance.toFixed(2)} from wallet + ${(product.price - walletBalance).toFixed(2)} from card
              </p>
            )}
          </div>
        )}

        {/* Payment Method Content */}
        {paymentMethod === 'digital' && paymentRequest ? (
          <div className="space-y-4">
            <div className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
              <PaymentRequestButtonElement
                options={{
                  paymentRequest,
                  style: {
                    paymentRequestButton: {
                      type: 'buy',
                      theme: 'dark',
                      height: '48px'
                    }
                  }
                }}
              />
            </div>
            <p className="text-xs text-center text-gray-500">
              Fast, secure checkout with Apple Pay or Google Pay
            </p>
          </div>
        ) : (
          <form onSubmit={handleCardPurchase} className="space-y-5">
            {/* Saved cards section */}
            {isLoadingCards ? (
              <div className="text-center py-4 text-gray-500">
                Loading payment methods...
              </div>
            ) : savedCards.length > 0 ? (
              <SavedCardSelector
                paymentMethods={savedCards}
                selectedId={selectedCardId}
                onSelect={setSelectedCardId}
                onAddNew={() => setSelectedCardId('new')}
              />
            ) : null}

            {/* New card form (shown if no saved cards OR "Add new card" selected) */}
            {(savedCards.length === 0 || selectedCardId === 'new') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Card Details</label>
                  <div className="border-2 border-gray-200 rounded-xl p-5 focus-within:border-pink-300 transition-colors" style={{boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)', minHeight: '56px'}}>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '17px',
                            color: '#1f2937',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            '::placeholder': { color: '#9ca3af' },
                            lineHeight: '24px'
                          }
                        }
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Secure payment powered by Stripe</p>
                </div>

                {/* Save card checkbox (pre-checked, opt-out) */}
                <label className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={saveCard}
                    onChange={(e) => setSaveCard(e.target.checked)}
                    className="w-4 h-4 text-pink-500 focus:ring-pink-500 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    Save card for future purchases
                  </span>
                </label>
              </>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold text-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!stripe || isProcessing || (selectedCardId === 'new' && !elements)}
                className="flex-1 px-6 py-3 text-white rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 duration-200 shadow-lg"
                style={{
                  background: isProcessing ? '#d1d5db' : 'linear-gradient(135deg, #ffc9e3 0%, #ffb3d9 100%)',
                  boxShadow: !isProcessing ? '0 4px 16px rgba(255, 105, 180, 0.3)' : 'none'
                }}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Unlock Now'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Cancel button for digital wallet */}
        {paymentMethod === 'digital' && paymentRequest && (
          <div className="mt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold text-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        )}
        </>)}
          </div>
        </div>
      </div>
    </>
  )

  // Render modal directly to document.body using portal to escape any parent opacity/styling
  return createPortal(modalContent, document.body)
}
